import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, Lock, CheckCircle, ChevronRight, Loader2, ArrowLeft, Clock, Brain, PenLine, Trophy, CircleDot } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import QuizPanel from "@/components/course/QuizPanel";
import ExercisePanel from "@/components/course/ExercisePanel";
import CourseComplete from "@/components/course/CourseComplete";
import { PageSEO, CourseSchema, FAQSchema, COURSE_FAQS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursePage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [previewLessons, setPreviewLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeTab, setActiveTab] = useState("video");
  const [progress, setProgress] = useState({});
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const res = await axios.get(`${API}/course/lessons`);
        if (!cancelled) setPreviewLessons(res.data.lessons || []);
      } catch (err) {
        if (!cancelled) toast.error("Could not load course preview.");
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, []);

  const buildProgressMap = (progressArr) => {
    const map = {};
    (progressArr || []).forEach((p) => {
      map[p.lesson_id] = { ...map[p.lesson_id], ...p };
    });
    return map;
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/course/verify-access`, { email });
      if (res.data.access) {
        setHasAccess(true);
        setLessons(res.data.lessons || []);
        setActiveLesson(res.data.lessons?.[0] || null);
        setProgress(buildProgressMap(res.data.progress));
        setNote(res.data.note || "");
        toast.success("Welcome back! Let's continue your journey.");
      } else {
        toast.error(res.data.message || "No access found.");
      }
    } catch (err) { toast.error("Verification failed."); }
    finally { setLoading(false); }
  };

  const saveProgress = async (lessonId, action, extra = {}) => {
    try {
      await axios.post(`${API}/course/progress`, { email, lesson_id: lessonId, action, ...extra });
      const res = await axios.get(`${API}/course/progress/${email}`);
      setProgress(buildProgressMap(res.data.progress));
    } catch {
      toast.error("Could not save progress. Please try again.");
    }
  };

  const completedCount = Object.values(progress).filter((p) => p.video_watched && p.quiz_completed && p.exercise_completed).length;
  const progressPercent = lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0;
  const displayLessons = hasAccess ? lessons : previewLessons;

  const getLessonStatus = (lessonId) => {
    const p = progress[lessonId];
    if (!p) return "not_started";
    if (p.video_watched && p.quiz_completed && p.exercise_completed) return "complete";
    if (p.video_watched || p.quiz_completed || p.exercise_completed) return "in_progress";
    return "not_started";
  };

  if (!hasAccess) {
    return (
      <main data-testid="course-page" className="pt-20">
        <PageSEO
          title="Burnout - To Balance In Action Course"
          description="A 10-lesson self-paced online course with video lessons, interactive quizzes, and emotional intelligence exercises. Recover from burnout and build lasting balance. R2,499."
          path="/course"
        />
        <CourseSchema />
        <FAQSchema faqs={COURSE_FAQS} />
        <section className="py-24 md:py-32 min-h-[80vh]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs mb-8 transition-colors hover:opacity-70" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                  <ArrowLeft size={12} /> Back to Home
                </Link>
                <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Online Course</p>
                <h1 className="font-serif text-4xl sm:text-5xl font-light leading-[1.1] mb-6" style={{ color: "var(--ll-text)" }}>
                  Burnout — To Balance <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>In Action</em>
                </h1>
                <p className="text-base leading-relaxed mb-4" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                  10 video lessons with interactive quizzes and emotional intelligence exercises. Self-paced, practical, and built for busy professionals.
                </p>
                <div className="flex gap-6 mb-8">
                  {[{icon: Play, label: "10 Video Lessons"}, {icon: Brain, label: "Interactive Quizzes"}, {icon: PenLine, label: "EQ Exercises"}].map((f) => (
                    <div key={f.label} className="flex items-center gap-2">
                      <f.icon size={14} style={{ color: "var(--ll-green)" }} />
                      <span className="text-xs" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{f.label}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-xs tracking-[0.2em] uppercase mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Course Curriculum</p>
                  {displayLessons.map((lesson, i) => (
                    <div key={lesson.id} data-testid={`lesson-preview-${lesson.id}`} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: i % 2 === 0 ? "var(--ll-bg-secondary)" : "transparent" }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--ll-bg-secondary)", border: "1px solid var(--ll-border)" }}>
                        <Lock size={12} style={{ color: "var(--ll-text-muted)" }} />
                      </div>
                      <p className="text-sm font-medium truncate flex-1" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{lesson.id}. {lesson.title}</p>
                      <span className="text-xs flex items-center gap-1 flex-shrink-0" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}><Clock size={10} /> {lesson.duration}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:sticky lg:top-28">
                <div className="p-8 rounded-2xl" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Access Your Course</h2>
                  <p className="text-sm mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Enter the email you used to purchase.</p>
                  <form onSubmit={handleVerify} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="course-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Purchase Email</Label>
                      <Input id="course-email" data-testid="course-email-input" type="email" placeholder="you@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border bg-white/50 focus:bg-white" style={{ borderColor: "var(--ll-border)" }} />
                    </div>
                    <button type="submit" data-testid="course-verify-btn" disabled={loading} className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                      {loading ? "Verifying..." : "Access My Course"}
                    </button>
                  </form>
                  <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--ll-border)" }}>
                    <p className="text-xs text-center mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Haven&apos;t purchased yet?</p>
                    <Link to="/#products" data-testid="course-purchase-link" className="w-full rounded-full px-6 py-3 text-sm font-medium border transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center" style={{ borderColor: "var(--ll-border)", color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                      Purchase Course — R2,499
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="course-page" className="pt-20">
      <section className="py-8 md:py-12 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Top Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-xs flex items-center gap-1 hover:opacity-70" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}><ArrowLeft size={12} /> Home</Link>
              <span style={{ color: "var(--ll-text-muted)" }}>/</span>
              <span className="text-xs font-medium" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Burnout To Balance In Action</span>
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-3">
              <div className="w-40 h-2 rounded-full overflow-hidden" style={{ background: "var(--ll-bg-secondary)" }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressPercent}%`, background: "var(--ll-green)" }} />
              </div>
              <span className="text-xs font-medium" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                {progressPercent}% complete
              </span>
              {progressPercent === 100 && <Trophy size={14} style={{ color: "var(--ll-accent)" }} />}
            </div>
          </div>

          {note && (
            <div className="mb-6 p-3 rounded-xl text-xs" style={{ background: "rgba(247,208,70,0.1)", color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Note: {note}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Lesson Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden lg:sticky lg:top-28" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                <div className="p-4" style={{ borderBottom: "1px solid var(--ll-border)" }}>
                  <h3 className="font-serif text-base font-medium" style={{ color: "var(--ll-text)" }}>Lessons</h3>
                  <p className="text-[11px] mt-0.5" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{completedCount}/{lessons.length} completed</p>
                </div>
                <div className="max-h-[550px] overflow-y-auto">
                  {lessons.map((lesson) => {
                    const isActive = activeLesson?.id === lesson.id;
                    const status = getLessonStatus(lesson.id);
                    return (
                      <button key={lesson.id} data-testid={`lesson-btn-${lesson.id}`} onClick={() => { setActiveLesson(lesson); setActiveTab("video"); }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-left transition-all duration-300"
                        style={{ background: isActive ? "var(--ll-bg-secondary)" : "transparent", borderBottom: "1px solid var(--ll-border)" }}>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: status === "complete" ? "var(--ll-green)" : isActive ? "var(--ll-cta)" : "var(--ll-bg-secondary)" }}>
                          {status === "complete" ? <CheckCircle size={12} style={{ color: "white" }} />
                            : isActive ? <Play size={10} style={{ color: "var(--ll-cta-text)" }} />
                            : status === "in_progress" ? <CircleDot size={10} style={{ color: "var(--ll-accent)" }} />
                            : <span className="text-[10px] font-medium" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{lesson.id}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate" style={{ color: isActive ? "var(--ll-text)" : "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{lesson.title}</p>
                          <p className="text-[10px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{lesson.duration}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              {activeLesson && (
                <div>
                  {/* Tab Navigation */}
                  <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
                    {[
                      { key: "video", label: "Video", icon: Play },
                      { key: "quiz", label: "Quiz", icon: Brain },
                      { key: "exercise", label: "Exercise", icon: PenLine },
                    ].map(({ key, label, icon: TabIcon }) => {
                      const isTabActive = activeTab === key;
                      const p = progress[activeLesson.id] || {};
                      const isDone = key === "video" ? p.video_watched : key === "quiz" ? p.quiz_completed : p.exercise_completed;
                      return (
                        <button key={key} data-testid={`tab-${key}`} onClick={() => setActiveTab(key)}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-medium transition-all duration-300"
                          style={{
                            background: isTabActive ? "white" : "transparent",
                            color: isTabActive ? "var(--ll-text)" : "var(--ll-text-muted)",
                            boxShadow: isTabActive ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                            fontFamily: "'Outfit', sans-serif",
                          }}>
                          <TabIcon size={14} />
                          {label}
                          {isDone && <CheckCircle size={11} style={{ color: "var(--ll-green)" }} />}
                        </button>
                      );
                    })}
                  </div>

                  {/* Video Tab */}
                  {activeTab === "video" && (
                    <div>
                      <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: "56.25%", background: "#1a1a1a" }}>
                        <iframe className="absolute inset-0 w-full h-full"
                          src={`https://www.youtube.com/embed/${activeLesson.youtube_id}?rel=0&modestbranding=1`}
                          title={activeLesson.title} frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                      <div className="mt-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div>
                          <p className="text-xs tracking-[0.2em] uppercase mb-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Lesson {activeLesson.id} of {lessons.length}</p>
                          <h2 className="font-serif text-2xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>{activeLesson.title}</h2>
                          <p className="text-sm leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{activeLesson.description}</p>
                        </div>
                        {!progress[activeLesson.id]?.video_watched && (
                          <button data-testid="mark-video-watched" onClick={() => saveProgress(activeLesson.id, "video_watched")}
                            className="flex-shrink-0 rounded-full px-5 py-2.5 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 flex items-center gap-1.5"
                            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
                            <CheckCircle size={12} /> Mark as Watched
                          </button>
                        )}
                        {progress[activeLesson.id]?.video_watched && (
                          <span className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-full"
                            style={{ background: "rgba(163,184,153,0.2)", color: "var(--ll-green)", fontFamily: "'Outfit', sans-serif" }}>
                            <CheckCircle size={12} /> Watched
                          </span>
                        )}
                      </div>
                      {/* Next step prompt */}
                      {progress[activeLesson.id]?.video_watched && !progress[activeLesson.id]?.quiz_completed && (
                        <div className="mt-6 p-4 rounded-xl flex items-center justify-between" style={{ background: "rgba(247,208,70,0.08)", border: "1px solid rgba(247,208,70,0.2)" }}>
                          <div className="flex items-center gap-3">
                            <Brain size={18} style={{ color: "var(--ll-accent)" }} />
                            <div>
                              <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>Ready to test your understanding?</p>
                              <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Take the quiz for this lesson.</p>
                            </div>
                          </div>
                          <button onClick={() => setActiveTab("quiz")} className="rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1" style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                            Start Quiz <ChevronRight size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quiz Tab */}
                  {activeTab === "quiz" && activeLesson.quiz && (
                    <QuizPanel
                      lesson={activeLesson}
                      progress={progress[activeLesson.id]}
                      onComplete={(score, total) => {
                        saveProgress(activeLesson.id, "quiz_completed", { quiz_score: score, quiz_total: total });
                        toast.success(`Quiz complete! ${score}/${total} correct.`);
                      }}
                    />
                  )}

                  {/* Exercise Tab */}
                  {activeTab === "exercise" && activeLesson.exercise && (
                    <ExercisePanel
                      lesson={activeLesson}
                      progress={progress[activeLesson.id]}
                      onComplete={(response) => {
                        saveProgress(activeLesson.id, "exercise_completed", { exercise_response: response });
                        toast.success("Exercise completed! Well done.");
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Course Completion */}
          <CourseComplete email={email} lessons={lessons} progress={progress} />
        </div>
      </section>
    </main>
  );
};

export default CoursePage;
